import React, { FC, useCallback } from 'react';

import { OnErrorHandler, PlayerApiError } from '../../types/error';

import { getPsid, clearPsid } from './lib/utils/psid';
import { useOnBeforeInit } from './hooks/useOnBeforeInit';
import { useOnError } from './hooks/useOnError';
import { useOnFinish } from './hooks/useOnFinish';

interface ChildrenProps {
    contentId: string;
    onStartCallback: () => void;
    handleFinish?: () => void;
    handleError?: OnErrorHandler;
    onClose?: () => void;
    psid: string;
}

export interface PlayerConfigProps {
    contentId: string;
    renderPlayer: (props: ChildrenProps) => React.ReactElement;
    onStart?: (contentId: string) => void;
    onFinish?: (contentId: string) => void;
    onError?: (contentId: string, error: PlayerApiError) => void;
    onBeforeInit?: () => void;
    onClose?: () => void;
}

export const Player: FC<PlayerConfigProps> = ({
    renderPlayer,

    contentId,
    onFinish,
    onStart,
    onBeforeInit,
    onError,
    onClose: onCloseExternal,
}) => {
    const psid = getPsid();

    const handleFinish = useOnFinish({
        contentId,
        onFinish,
    });

    const handleError = useOnError({
        contentId,
        onError,
    });

    useOnBeforeInit({
        isReadyToInit: Boolean(contentId),
        onBeforeInit,
    });

    const onClose = useCallback(() => {
        onCloseExternal?.();
        clearPsid();
    }, [onCloseExternal]);

    const onStartCallback = useCallback(() => onStart?.(contentId || ''), [contentId, onStart]);

    if (contentId === undefined) {
        return null;
    }

    return renderPlayer({ contentId, onStartCallback, handleError, handleFinish, psid, onClose });
};
